/**
 * PostHog Analytics Events and Properties
 * Used for tracking customer journey, funnel conversion, and churn analysis
 */

// Funnel Events - for tracking conversion stages
export enum FunnelEvent {
  // Landing & Onboarding Funnel
  LANDING_PAGE_VIEWED = "landing_page_viewed",
  FEATURE_EXPLORED = "feature_explored",
  REQUEST_FORM_STARTED = "request_form_started",

  // Request Form Funnel
  REQUEST_FORM_STEP_STARTED = "request_form_step_started",
  REQUEST_FORM_STEP_COMPLETED = "request_form_step_completed",
  REQUEST_FORM_ABANDONED = "request_form_abandoned",
  REQUEST_FORM_SUBMITTED = "request_form_submitted",
  REQUEST_SUBMISSION_SUCCESS = "request_submission_success",
  REQUEST_SUBMISSION_FAILED = "request_submission_failed",

  // Blog & Content Engagement
  BLOG_POST_VIEWED = "blog_post_viewed",
  BLOG_POST_SHARED = "blog_post_shared",
  CONTENT_DOWNLOADED = "content_downloaded",
}

// User Behavior Events - for engagement tracking
export enum UserEvent {
  // Navigation
  PAGE_NAVIGATED = "page_navigated",
  SEARCH_PERFORMED = "search_performed",
  FILTER_APPLIED = "filter_applied",

  // Form Interactions
  FORM_FIELD_FOCUSED = "form_field_focused",
  FORM_FIELD_FILLED = "form_field_filled",
  FORM_FIELD_ERROR = "form_field_error",
  FORM_VALIDATION_FAILED = "form_validation_failed",
  FORM_STEP_NAVIGATED_BACK = "form_step_navigated_back",

  // Error Handling
  ERROR_ENCOUNTERED = "error_encountered",
  ERROR_RESOLVED = "error_resolved",
}

// Retention Events - for churn analysis
export enum RetentionEvent {
  USER_SIGNED_UP = "user_signed_up",
  USER_RETURNED = "user_returned",
  USER_SESSION_STARTED = "user_session_started",
  USER_SESSION_ENDED = "user_session_ended",
  USER_ENGAGED = "user_engaged",
  USER_INACTIVE = "user_inactive",
  USER_CHURNED = "user_churned",
}

// Feature Usage Events
export enum FeatureEvent {
  FEATURE_USED = "feature_used",
  FEATURE_FIRST_USE = "feature_first_use",
}

export type FlagValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | null;

export interface FlagVariant {
  key: string;
  isActive: boolean;
  value?: FlagValue;
  rolloutPercentage?: number;
}

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  value?: FlagValue;
  variant?: FlagVariant;
  createdAt?: string;
  updatedAt?: string;
}

export const KNOWN_FLAGS = {
  ENHANCED_FORM_VALIDATION: "enhanced_form_validation",
  NEW_COMPANY_NAME_FIELD: "new_company_name_field",
  ADVANCED_CAREER_FILTER: "advanced_career_filter",
  PHONE_FIELD_OPTIONAL: "phone_field_optional",
  NEW_SEARCH_FILTER: "new_search_filter",
  ADVANCED_SEARCH_MODAL: "advanced_search_modal",
  SEARCH_SUGGESTIONS: "search_suggestions",
  DARK_MODE: "dark_mode",
  NEW_PRICING_PAGE: "new_pricing_page",
  BETA_ANALYTICS_DASHBOARD: "beta_analytics_dashboard",
  EXPORT_TO_CSV: "export_to_csv",
} as const;

export type KnownFlagKey = (typeof KNOWN_FLAGS)[keyof typeof KNOWN_FLAGS];

export interface UserProperties {
  email?: string;
  personName?: string;
  companyName?: string;
  source?: string;
  daysActive?: number;
  lastActivityDate?: string;
  signupDate?: string;
  conversionStage?: "visitor" | "lead" | "customer" | "churned";
  totalFormSubmissions?: number;
  totalPageViews?: number;
  totalSessionsCount?: number;
  averageSessionDuration?: number;
}

export interface EventProperties {
  [key: string]: string | number | boolean | null | undefined;
  timestamp?: string;
  userAgent?: string;
  pageUrl?: string;
  formName?: string;
  formStep?: number;
  formField?: string;
  formFieldValue?: string | number;
  isFormValid?: boolean;
  timeOnFormSeconds?: number;
  timeOnStepSeconds?: number;
  totalTimeSpentSeconds?: number;
  completionPercentage?: number;
  errorCode?: string;
  errorMessage?: string;
  errorType?: string;
  featureName?: string;
  featureCategory?: string;
  isFirstUse?: boolean;
  sourceUrl?: string;
  targetUrl?: string;
  referrer?: string;
  engagementScore?: number;
  contentCategory?: string;
}

export interface ChurnIndicators {
  daysSinceLastVisit: number;
  daysSinceLastFormSubmission: number;
  totalPageViews: number;
  totalFormAttempts: number;
  formCompletionRate: number;
  averageSessionDuration: number;
  totalSessions: number;
  hasCompletedForm: boolean;
  hasVisitedMultiplePages: boolean;
  hasShownInterest: boolean;
  churnProbability: "high" | "medium" | "low";
  churnReason?: string;
}

// Cohort Definitions
export interface CohortDefinition {
  name: string;
  description: string;
  criteria: string[];
}

export const PREDEFINED_COHORTS: Record<string, CohortDefinition> = {
  ACTIVE_USERS: {
    name: "Active Users",
    description: "Users with activity in the last 7 days",
    criteria: ["last_activity_within_7_days"],
  },
  INACTIVE_USERS: {
    name: "Inactive Users",
    description: "Users with no activity in the last 30 days",
    criteria: ["last_activity_over_30_days_ago"],
  },
  FORM_COMPLETERS: {
    name: "Form Completers",
    description: "Users who have completed the request form",
    criteria: ["form_completed"],
  },
  FORM_ABANDONERS: {
    name: "Form Abandoners",
    description: "Users who started but did not complete the form",
    criteria: ["form_abandoned"],
  },
  HIGH_ENGAGEMENT: {
    name: "High Engagement",
    description: "Users with above-average engagement score",
    criteria: ["engagement_score_above_75"],
  },
  AT_RISK: {
    name: "At-Risk Users",
    description: "Users showing churn signals",
    criteria: ["churn_probability_high", "last_activity_over_14_days"],
  },
  NEW_USERS: {
    name: "New Users",
    description: "Users who signed up in the last 7 days",
    criteria: ["signup_within_7_days"],
  },
};

export const EVENT_DEFINITIONS = {
  [FunnelEvent.REQUEST_FORM_STARTED]: {
    description: "User initiates request form submission",
    triggerTiming: "Form component mount, step === 0",
    requiredProperties: ["formName", "totalSteps"],
    optionalProperties: [],
    deduplicationKey: (props?: EventProperties) =>
      `request_form_started_${props?.formName}`,
  },
  [FunnelEvent.REQUEST_FORM_STEP_STARTED]: {
    description: "User enters a specific form step",
    triggerTiming: "Form step changes (currentStep > previousStep)",
    requiredProperties: ["formName", "formStep", "totalSteps"],
    optionalProperties: ["completionPercentage"],
    deduplicationKey: (props?: EventProperties) =>
      `form_step_started_${props?.formName}_${props?.formStep}`,
  },
  [FunnelEvent.REQUEST_FORM_STEP_COMPLETED]: {
    description: "User completes a form step successfully",
    triggerTiming: "User moves to next step or submits form",
    requiredProperties: ["formName", "formStep", "totalSteps"],
    optionalProperties: ["timeOnStepSeconds", "completionPercentage"],
    deduplicationKey: (props?: EventProperties) =>
      `form_step_completed_${props?.formName}_${props?.formStep}`,
  },
  [FunnelEvent.REQUEST_FORM_ABANDONED]: {
    description: "User abandons form before completion",
    triggerTiming:
      "Component unmount before form completion OR explicit abandonment",
    requiredProperties: ["formName", "formStep", "totalSteps"],
    optionalProperties: ["timeOnFormSeconds", "completionPercentage"],
    deduplicationKey: (props?: EventProperties) =>
      `form_abandoned_${props?.formName}_${props?.formStep}`,
  },
  [FunnelEvent.REQUEST_SUBMISSION_SUCCESS]: {
    description: "Form submission completed successfully",
    triggerTiming: "API call returns success (200-299)",
    requiredProperties: ["formName", "totalSteps"],
    optionalProperties: ["totalTimeSpentSeconds", "completionPercentage"],
    deduplicationKey: (props?: EventProperties) =>
      `submission_success_${props?.formName}_${new Date().getTime()}`,
  },
  [UserEvent.FORM_FIELD_FOCUSED]: {
    description: "User focuses on a form input field",
    triggerTiming: "onFocus event",
    requiredProperties: ["formName", "formField"],
    optionalProperties: ["formStep"],
    deduplicationKey: (props?: EventProperties) =>
      `field_focused_${props?.formName}_${props?.formField}`,
  },
  [UserEvent.FORM_FIELD_FILLED]: {
    description: "User fills in a form field value",
    triggerTiming: "onChange event + blur (debounced 500ms)",
    requiredProperties: ["formName", "formField"],
    optionalProperties: ["formStep", "isFormValid"],
    deduplicationKey: (props?: EventProperties) =>
      `field_filled_${props?.formName}_${props?.formField}`,
  },
  [UserEvent.FORM_FIELD_ERROR]: {
    description: "Form field validation fails",
    triggerTiming: "Validation check fails",
    requiredProperties: ["formName", "formField", "errorMessage"],
    optionalProperties: ["errorType", "formStep"],
    deduplicationKey: (props?: EventProperties) =>
      `field_error_${props?.formName}_${props?.formField}_${new Date().getTime()}`,
  },
  [UserEvent.FORM_STEP_NAVIGATED_BACK]: {
    description: "User navigates back to previous form step",
    triggerTiming: "Back button click",
    requiredProperties: ["formName", "fromStep", "toStep"],
    optionalProperties: ["timeOnStepSeconds"],
    deduplicationKey: (props?: EventProperties) =>
      `step_back_${props?.formName}_${props?.fromStep}`,
  },
  [RetentionEvent.USER_SESSION_STARTED]: {
    description: "User session starts (page loaded)",
    triggerTiming: "useSessionTracking hook mount",
    requiredProperties: [],
    optionalProperties: ["sessionId"],
    deduplicationKey: () => `session_start_${new Date().getTime()}`,
  },
  [RetentionEvent.USER_SESSION_ENDED]: {
    description: "User session ends (page unload)",
    triggerTiming: "beforeunload event",
    requiredProperties: [],
    optionalProperties: ["totalTimeSpentSeconds"],
    deduplicationKey: () => `session_end_${new Date().getTime()}`,
  },
  [RetentionEvent.USER_RETURNED]: {
    description: "User returns after 1+ days of inactivity",
    triggerTiming: "Session start with 1+ day gap from last session",
    requiredProperties: [],
    optionalProperties: ["daysSinceLastSession"],
    deduplicationKey: () => `user_returned_${new Date().getDate()}`,
  },
} as const;
